import get from 'lodash.get';
import findIndex from 'lodash.findindex';
import cloneDeep from 'lodash.clonedeep';
import find from 'lodash.find';
import localforage from 'localforage';
import { v4 as uuidv4 } from 'uuid';
import { datadogRum } from '@datadog/browser-rum';

import {
  lockRefresh,
  unlockRefresh,
  isLocked,
  lockExpired,
} from '@/utilities/JwtRefreshTokensLock';
import { getDifferenceInMinutes } from '@/utilities/DateUtils';
import { decodeJwt } from '@/utilities';
import MultibuyerToolsClient from '@/api/MultibuyerToolsClient';
import CapitalClient from '@/api/CapitalClient';
import UserClient from '../../api/UserClient';
import IdentityClient from '../../api/IdentityClient';
import User from '../../models/User';
import { isWithinStockwave, isInIframe } from '../../utilities/iFrameDetection';

const tokenKey = 'acv-jwt-tokens';
const appUuid = uuidv4();

function checkForSearchVal(stringToSearchFor, stringOrArrayToParse) {
  if (Array.isArray(stringOrArrayToParse)) {
    return !!stringOrArrayToParse.filter((ar) => ar.includes(stringToSearchFor)).length;
  }
  if (typeof stringOrArrayToParse === 'string') {
    return stringOrArrayToParse.includes(stringToSearchFor);
  }
  return false;
}

const authorization = {
  state: {
    errors: [],
    user: {},
    ready: false,
    settings: [],
    goToAfterLogin: '',
    refreshTokenRequest: null,
    jwtTokenExpirationDateTime: null,
    userAppliedSavedSearchIds: [],
    carfaxTokens: null,
  },
  mutations: {
    setIsLimitedUser(state, isLimitedUserVal) {
      state.user.limited_user = isLimitedUserVal;
    },
    setUserRole(state, userRoleVal) {
      state.user.role = userRoleVal;
    },
    setIsVci(state, isVciVal) {
      state.user.is_vci = isVciVal;
    },
    setIsLaunchGtdSale(state, isLaunchGtdSaleVal) {
      state.user.is_launch_gtd_sale = isLaunchGtdSaleVal;
    },
    setAppRole(state, appRole) {
      state.user.app_role = appRole;
    },
    setCarfaxTokens(state, carfaxTokens) {
      state.carfaxTokens = carfaxTokens;
    },
    setUserAppliedSavedSearchIds(state, userAppliedSavedSearchIds = []) {
      state.userAppliedSavedSearchIds = [
        ...userAppliedSavedSearchIds,
      ];
    },
    setRefreshTokenRequest(state, request) {
      state.refreshTokenRequest = request;
    },
    clearRefreshTokenRequest(state) {
      state.refreshTokenRequest = null;
    },
    setErrors(state, errors) {
      state.errors = errors;
    },
    setUser(state, user) {
      state.user = new User(user);
    },
    setReady(state, ready) {
      state.ready = ready;
    },
    setUserSettings(state, { settings, category }) {
      if (category) {
        const settingIdx = findIndex(state.settings, { category });
        if (settingIdx >= 0) {
          state.settings = state.settings.map(
            (setting, idx) => (idx === settingIdx ? settings.categories[0] : setting),
          );
        } else {
          state.settings = [
            ...state.settings,
            settings.categories[0],
          ];
        }
      } else {
        state.settings = settings.categories;
      }
    },
    setGoToAfterLogin(state, goToAfterLogin) {
      state.goToAfterLogin = goToAfterLogin;
    },
    setJwtTokenExpirationDateTime(state, jwtTokenExpirationDateTime) {
      state.jwtTokenExpirationDateTime = jwtTokenExpirationDateTime;
    },
    setJwtTokens(state, tokens) {
      window.localStorage.setItem(tokenKey, JSON.stringify({
        ...tokens,
        lastUpdated: new Date(),
      }));
    },
  },
  actions: {
    async setIsCapitalUser({ commit, state, dispatch }) {
      const tokens = await dispatch('getJwtTokens');
      let capitalUser = null;
      const userCopy = cloneDeep(state.user);
      userCopy.capitalUserFailed = true;
      if (tokens.jwtToken) {
        userCopy.capitalUserFailed = false;
        const capitalClient = new CapitalClient();
        try {
          capitalUser = await capitalClient.isUserCapital();
        } catch (error) {
          // if error is not 404 then the endpoint failed and is not defined if user
          // is or not a capital user.
          if (error.response?.status !== 404) {
            // this flag is used to call the exists endpoint again or redirect to
            // capital-portal-error cause is not defined if the user is or not a capital user.
            userCopy.capitalUserFailed = true;
          }
          // if error is 404, user is a non capital user and can enter to the capital landing page.
        } finally {
          userCopy.isCapitalUser = !!capitalUser;
          userCopy.capitalUser = capitalUser;
          commit('setUser', userCopy);
        }
      }
      return capitalUser;
    },
    setCarfaxTokens({ commit }, carfaxTokens) {
      commit('setCarfaxTokens', carfaxTokens);
    },
    setJwtTokens(
      {
        commit,
      },
      {
        jwtToken,
        refreshToken,
        lastRefreshFailed = false,
      },
    ) {
      const decodedJwtToken = decodeJwt(jwtToken) ?? '';
      const isVciVal = checkForSearchVal('vci', decodedJwtToken?.app_role)
        || checkForSearchVal('sia_insp_', decodedJwtToken?.app_role);
      const isLaunchGtdSale = checkForSearchVal('launch_gtd_sale', decodedJwtToken?.app_role);
      const limitedUserVal = !!(decodedJwtToken?.app_role?.includes('readonly'));
      commit('setJwtTokens', {
        jwtToken,
        refreshToken,
        lastRefreshFailed,
      });
      commit('setIsLimitedUser', limitedUserVal);
      commit('setIsVci', isVciVal);
      commit('setIsLaunchGtdSale', isLaunchGtdSale);
      commit('setAppRole', decodedJwtToken?.app_role ?? []);
    },
    setUserAppliedSavedSearchIds({ commit }, userAppliedSavedSearchIds = []) {
      commit('setUserAppliedSavedSearchIds', userAppliedSavedSearchIds);
    },
    updateReady({ commit }, ready) {
      commit('setReady', ready);
    },
    setErrors({ commit }, errors) {
      commit('setErrors', errors);
    },
    clearUserSettings({ commit }) {
      commit('setUserSettings', { settings: { categories: [] } });
    },
    async backendLogout({ state, dispatch }) {
      const { refreshToken } = await dispatch('getJwtTokens');
      const userClient = new UserClient(state.user);
      try {
        await userClient.logout(refreshToken);
      } catch (err) {
        // do nothing to continue clear user
      }
    },
    async clearUser({ commit, dispatch }) {
      await dispatch('backendLogout');
      commit('setUser', {});
      await dispatch('clearUserSettings');
      commit('setUserAppliedSavedSearchIds', []);
      commit('clearRefreshTokenRequest');
      commit('setJwtTokenExpirationDateTime', null);
      await dispatch('clearTosInfo');
      await dispatch('updateAuctionAccessIssues', { allIssues: [], dealerIssues: [], personIssues: [] });
      if (import.meta.env.VITE_THIRD_PARTY_JS_ENABLED === 'true') {
        datadogRum.clearUser();
      }
      window.localStorage.removeItem(tokenKey);
    },
    async checkUserIsLimited({ dispatch }) {
      await dispatch('refreshUser');

      window.setTimeout(() => {
        if (this.getters.isAuthorized) {
          dispatch('checkUserIsLimited');
        }
      }, import.meta.env.VITE_LIMITED_USER_CHECK_INTERVAL || 840000);
    },
    async loginUser({
      commit,
      dispatch,
    }, { email, password, reCaptchaToken }) {
      try {
        const identityClient = new IdentityClient();
        const shouldSkipRecaptcha = isInIframe() && isWithinStockwave();
        const userData = await identityClient.hydraLegacyLogin(
          email,
          password,
          shouldSkipRecaptcha ? null : reCaptchaToken,
        );
        const { jwtToken, refreshToken } = userData.data;
        const decodedJwtToken = decodeJwt(jwtToken);
        const isVciVal = checkForSearchVal('vci', decodedJwtToken?.app_role)
          || checkForSearchVal('sia_insp_', decodedJwtToken?.app_role);
        const isLaunchGtdSale = checkForSearchVal('launch_gtd_sale', decodedJwtToken?.app_role);
        const limitedUserVal = !!(decodedJwtToken?.app_role?.includes('readonly'));
        delete userData.limited_user;
        const user = {
          ...userData.data.data,
          limited_user: limitedUserVal,
          is_vci: isVciVal,
          is_launch_gtd_sale: isLaunchGtdSale,
        };

        await dispatch('setJwtTokens', {
          jwtToken,
          refreshToken,
        });

        const issues = {
          allIssues: [],
          dealerIssues: [],
          personIssues: [],
        };
        if (userData.data.data !== undefined) {
          issues.allIssues = userData.data.data.issues ?? [];
          issues.dealerIssues = issues.allIssues.filter((issue) => issue.affectedEntities.some((entity) => entity.entityType === 'Company'));
          issues.personIssues = issues.allIssues.filter((issue) => issue.affectedEntities.some((entity) => entity.entityType === 'Person'));
        }
        try {
          const multibuyerToolsClient = new MultibuyerToolsClient(user);
          const multibuyerDealershipsResponse = await multibuyerToolsClient
            .fetchDealershipsThatCanPlaceHoldOnAuctions();
          user.multibuyerDealerships = multibuyerDealershipsResponse.data.data;
        } catch {
          user.multibuyerDealerships = [];
        }

        commit('setUser', user);
        commit('setAppRole', decodedJwtToken?.app_role ?? []);
        await dispatch('updateAuctionAccessIssues', issues);
        await dispatch('getAllFeatureFlags');
        await dispatch('checkIfNeedTosAcceptance');
      } catch (error) {
        commit('setErrors', [error]);
        throw error;
      }
    },
    async ssoLoginUser({
      commit,
      dispatch,
    }, { ssoJwtToken }) {
      try {
        const identityClient = new IdentityClient();
        const userData = await identityClient.ssoLogin(ssoJwtToken);
        const { jwtToken, refreshToken } = userData.data;
        const decodedJwtToken = decodeJwt(jwtToken);
        const isVciVal = checkForSearchVal('vci', decodedJwtToken?.app_role)
          || checkForSearchVal('sia_insp_', decodedJwtToken?.app_role);
        const isLaunchGtdSale = checkForSearchVal('launch_gtd_sale', decodedJwtToken?.app_role);
        const limitedUserVal = !!(decodedJwtToken?.app_role?.includes('readonly'));
        const user = {
          ...userData.data.data,
          limited_user: limitedUserVal,
          is_vci: isVciVal,
          is_launch_gtd_sale: isLaunchGtdSale,
        };
        await dispatch('setJwtTokens', {
          jwtToken,
          refreshToken,
        });

        try {
          const multibuyerToolsClient = new MultibuyerToolsClient(user);
          const multibuyerDealershipsResponse = await multibuyerToolsClient
            .fetchDealershipsThatCanPlaceHoldOnAuctions();
          user.multibuyerDealerships = multibuyerDealershipsResponse.data.data;
        } catch {
          user.multibuyerDealerships = [];
        }

        commit('setUser', user);
        commit('setAppRole', decodedJwtToken?.app_role ?? []);
        await dispatch('getAllFeatureFlags');
        await dispatch('checkIfNeedTosAcceptance');
      } catch (error) {
        commit('setErrors', [error]);
        throw error;
      }
    },
    async refreshUser({ commit, dispatch, state }) {
      const userClient = new UserClient(state.user);
      let limitedUserVal;
      let isVciVal;
      let isLaunchGtdSale = false;
      let userIdVal = state.user.user_id;
      const tokens = await dispatch('getJwtTokens');
      const decodedJwtToken = decodeJwt(tokens.jwtToken);

      if (userIdVal === undefined && decodedJwtToken.acvauctions_legacy_user_id !== undefined) {
        userIdVal = decodedJwtToken.acvauctions_legacy_user_id;
      }
      if (decodedJwtToken.app_role !== undefined) {
        isVciVal = checkForSearchVal('vci', decodedJwtToken?.app_role)
          || checkForSearchVal('sia_insp_', decodedJwtToken?.app_role);
        limitedUserVal = !!(decodedJwtToken?.app_role?.includes('readonly'));
        isLaunchGtdSale = checkForSearchVal('launch_gtd_sale', decodedJwtToken?.app_role);
      }

      const [
        userLoginDataResponse,
        userResponse,
      ] = await Promise.all([
        userClient.getUserLoginData(userIdVal),
        // TODO - Updated login/login_data to return the zip code to save on calls - ETC
        userClient.getUser(userIdVal),
      ]);

      const {
        data: {
          data: {
            user: {
              zipCode,
            },
          },
        },
      } = userResponse;

      delete userLoginDataResponse.limited_user;

      commit('setUser', {
        ...state.user,
        ...userLoginDataResponse.data.data,
        zip_code: zipCode,
      });
      if (limitedUserVal !== undefined) {
        commit('setIsLimitedUser', limitedUserVal);
      }
      if (isVciVal !== undefined) {
        commit('setIsVci', isVciVal);
      }
      if (isLaunchGtdSale !== undefined) {
        commit('setIsLaunchGtdSale', isLaunchGtdSale);
      }
      commit('setAppRole', decodedJwtToken?.app_role ?? []);
    },
    checkOnExistingRequest({
      commit,
      state,
      dispatch,
    }, {
      beforeUpdateJwtToken,
      resolve,
      reject,
    }) {
      dispatch('getJwtTokens')
        .then((afterUpdateTokens) => {
          const refreshIsLocked = isLocked();
          const refreshIsExpired = lockExpired(); // TODO maybe we don't need this
          const {
            jwtToken: afterUpdateJwtToken,
          } = afterUpdateTokens;
          if (beforeUpdateJwtToken === afterUpdateJwtToken) {
            if (!refreshIsLocked || refreshIsExpired) {
              dispatch('callTokenRefresh')
                .then(({ refreshedUser }) => {
                  resolve(refreshedUser);
                })
                .catch((err) => {
                  commit('clearRefreshTokenRequest');
                  reject(err);
                });
            } else {
              reject(new Error('Timed out waiting for token refresh'));
              commit('clearRefreshTokenRequest');
            }
          } else {
            resolve(state.user);
            commit('clearRefreshTokenRequest');
          }
        });
    },
    waitForOtherWindowToRefreshJwtToken({
      commit,
      state,
      dispatch,
    }) {
      if (!state.refreshTokenRequest) {
        const existingRequest = new Promise((resolve, reject) => {
          dispatch('getJwtTokens')
            .then((beforeUpdateTokens) => {
              const {
                jwtToken: beforeUpdateJwtToken,
              } = beforeUpdateTokens;
              dispatch('checkOnExistingRequest', { beforeUpdateJwtToken, resolve, reject });
            });
        });
        commit('setRefreshTokenRequest', existingRequest);
      }
      return state.refreshTokenRequest;
    },
    async callTokenRefresh({
      commit,
      state,
      dispatch,
    }) {
      let result = {};
      const tokens = await dispatch('getJwtTokens');
      const {
        lastRefreshFailed = false,
      } = tokens;

      if (!lastRefreshFailed) {
        try {
          lockRefresh(appUuid);
          const identityClient = new IdentityClient(state.user);
          let refreshResponse;

          if (tokens.refreshToken !== undefined) {
            refreshResponse = await identityClient.refreshToken(tokens);
          }

          /* eslint-disable-next-line vue/max-len */
          const { jwtToken, refreshToken, issues: aaIssues = [] } = refreshResponse.data || {};
          const dealerIssues = aaIssues.filter((issue) => issue.affectedEntities.some((entity) => entity.entityType === 'Company'));
          const personIssues = aaIssues.filter((issue) => issue.affectedEntities.some((entity) => entity.entityType === 'Person'));
          const newTokens = {
            jwtToken,
            refreshToken,
          };
          const refreshedUser = { ...state.user };
          commit('setUser', refreshedUser);
          delete refreshedUser.limited_user;
          await dispatch('setJwtTokens', newTokens);
          await dispatch('clearFeatureFlagClient');
          commit('clearRefreshTokenRequest');
          result = {
            user: refreshedUser,
            tokens: newTokens,
          };
          await dispatch('updateAuctionAccessIssues', {
            allIssues: aaIssues,
            dealerIssues,
            personIssues,
          });
        } finally {
          unlockRefresh();
        }
      }
      return result;
    },
    refreshJwtTokens({
      commit,
      state,
      dispatch,
      getters,
    }, {
      force = false,
    } = {}) {
      try {
        if (!state.refreshTokenRequest) {
          const existingRequest = new Promise((resolve, reject) => {
            dispatch('getJwtTokens')
              .then((tokens) => {
                const currentDate = new Date();
                const {
                  lastUpdated,
                } = tokens;

                const differenceInMinutes = getDifferenceInMinutes(
                  currentDate,
                  lastUpdated,
                );
                if (getters.isAuthorized) {
                  if (force || differenceInMinutes > 12) {
                    dispatch('callTokenRefresh')
                      .then(({ refreshedUser }) => {
                        resolve(refreshedUser);
                        commit('clearRefreshTokenRequest');
                      })
                      .catch((err) => {
                        reject(err);
                        commit('clearRefreshTokenRequest');
                      });
                  } else {
                    resolve(state.user);
                    commit('clearRefreshTokenRequest');
                  }
                } else {
                  resolve(state.user);
                  commit('clearRefreshTokenRequest');
                }
              });
          });
          commit('setRefreshTokenRequest', existingRequest);
          return existingRequest;
        }

        return state.refreshTokenRequest;
      } catch (err) {
        commit('clearRefreshTokenRequest');
        return Promise.reject(err);
      }
    },
    async getUserSettings({ state, commit }, { category = '' }) {
      const userClient = new UserClient(state.user);
      const settings = await userClient.getUserSettings(category, false);

      commit('setUserSettings', { settings: settings.data.data, category });

      return state.settings;
    },
    setGoToAfterLogin({ commit }, goToAfterLogin) {
      commit('setGoToAfterLogin', goToAfterLogin);
    },
    clearGoToAfterLogin({ commit }) {
      commit('setGoToAfterLogin', '');
    },
    getJwtTokens() {
      const tokensJSON = window.localStorage.getItem(tokenKey);
      let tokens;
      if (tokensJSON) {
        tokens = JSON.parse(tokensJSON);
        tokens.lastUpdated = new Date(tokens.lastUpdated);
      }
      return tokens || {};
    },
    async refreshCarfaxTokensFromLocalForage({
      commit,
    }) {
      const {
        authorization: {
          carfaxTokens,
        },
      } = await localforage.getItem('acv-user-storage');
      if (carfaxTokens) {
        commit('setCarfaxTokens', carfaxTokens);
      }
    },
    logoutCarfax({ commit }) {
      commit('setCarfaxTokens', null);
    },
    async getExtendedInfoToken({ state }) {
      const identityClient = new IdentityClient(state.user);
      const response = await identityClient.getExtendedInfoToken();
      return response.data.token;
    },
    async setCreditBalanceInfo({ commit, state, dispatch }) {
      const tokens = await dispatch('getJwtTokens');
      let creditBalanceInfo = {};
      if (tokens.jwtToken) {
        try {
          const userClient = new UserClient(state.user);
          creditBalanceInfo = await userClient.getCreditBalanceInfo();
        } catch (ex) {
          dispatch('setError', ex);
        }
        const userCopy = cloneDeep(state.user);
        userCopy.creditBalanceInfo = creditBalanceInfo?.data?.data ?? {};
        commit('setUser', userCopy);
      }
      return creditBalanceInfo;
    },
    async loginNativeUser({
      commit,
      dispatch,
    }, {
      userId, jwtToken, refreshToken,
    }) {
      try {
        await dispatch('setJwtTokens', {
          jwtToken,
          refreshToken,
        });

        let response;
        try {
          response = await new UserClient({}).getUser(userId, true, false, true);
        } catch (err) {
          throw new Error('Could not authenticate', err);
        }

        const mockUser = {
          id: userId,
          user_id: userId,
          api_tokens: {
            jwt_token: jwtToken,
            refresh_token: refreshToken,
          },
        };

        const userData = {
          ...response.data.data.user,
          dealerships: response.data.data.dealerships,
          ...mockUser,
        };

        try {
          const multiBuyerToolsClient = new MultibuyerToolsClient(userData);
          const multiBuyerDealershipsResponse = await multiBuyerToolsClient
            .fetchDealershipsThatCanPlaceHoldOnAuctions();
          userData.multibuyerDealerships = multiBuyerDealershipsResponse.data.data;
        } catch {
          userData.multibuyerDealerships = [];
        }

        commit('setUser', userData);
        await dispatch('fetchFeatureFlagsFromAPI');
      } catch (error) {
        commit('setErrors', [error]);
        throw error;
      }
    },
  },
  getters: {
    authorizationIsReady(state) {
      return state.ready;
    },
    currentUser(state, getters) {
      return new User({
        ...state.user,
        isVCI: getters.isVCI,
        isVci: getters.isVci,
        isLimitedUser: getters.isLimitedUser,
      });
    },
    appRoles(state) {
      return state.user.app_role ?? [];
    },
    isAuthorized(state) {
      return !!state.user.user_id;
    },
    isAdmin(state) {
      return state.user.isAdmin;
    },
    memberType(state) {
      return state.user.memberType;
    },
    isVCI(state) {
      return state.user?.is_vci || false;
    },
    isLaunchGtdSale(state) {
      return state.user?.is_launch_gtd_sale || false;
    },
    canSell(state) {
      return get(state.user, 'canSell');
    },
    hasMultipleDealerships(state) {
      return get(state.user, 'hasMultipleDealerships', false);
    },
    assignedDealershipIds(state) {
      return get(state.user, 'dealerships', []);
    },
    isLimitedUser(state) {
      return !!state.user?.limited_user;
    },
    userRole(state) {
      return state.user?.role || 'USER';
    },
    userSettings(state) {
      return (category) => {
        const settingCategory = find(state.settings, (setting) => setting.category === category);
        return settingCategory ? settingCategory.settings : [];
      };
    },
    proxyDuration(state) {
      let proxyDuration = {
        value: '10',
        description: '10 Days',
      };

      const proxyDurationSetting = state.settings[0]?.settings.find((setting) => setting.settingKey === 'custom_persistent_proxy_length');
      if (proxyDurationSetting !== undefined) {
        const duration = proxyDurationSetting.options.find((option) => option.isSelected);
        if (duration !== undefined) {
          proxyDuration = {
            value: duration.value,
            description: duration.title,
          };
        }
      }

      return proxyDuration;
    },
    hasCreditBalanceInfo(state) {
      return state.user.creditBalanceInfo?.banner_info?.banner_message?.trim().length > 0
        && Number.parseInt(state.user.creditBalanceInfo?.credit_balance, 10) > 0;
    },
    showCapitalMenu(state, getters, rootState) {
      // Is a capital user with floorplan dealers
      // eslint-disable-next-line vue/max-len

      const { flags, featureFlagSlugs } = rootState?.featureFlags || {};
      const capitalShowAdvantageLeadGen = flags?.[featureFlagSlugs?.capitalShowAdvantageLeadGen];
      const { isCapitalUser } = state.user;

      const isCapitalWithFloorplanDealers = state.user?.capitalDealerSelectionList?.length > 0
        && isCapitalUser;

      const isCapitalAdvantageOnly = capitalShowAdvantageLeadGen
        && isCapitalUser
        && !isCapitalWithFloorplanDealers
        // This check if all dealers in the list of capital dealers have only ADVANTAGE product line
        && Object.entries(state.user?.capitalUser?.dealerships || {})
          .every(([, value]) => value?.product_lines?.buyer?.every((product) => product === 'ADVANTAGE'));

      // Is active and is not a capital user
      const hasDealerships = !!Object.keys(state.user?.dealerships || {}).length;
      const activeNotCapital = hasDealerships && !isCapitalUser;

      return (isCapitalWithFloorplanDealers || activeNotCapital || isCapitalAdvantageOnly);
    },
  },
};

export default authorization;
